import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'

import {
	Breadcrumb,
	Input,
	InputGroup,
	PageTitle,
	Pagination,
	Template,
} from '@components'
import Button from '@components/Button'
import Divider from '@components/Divider'
import EmptyRow from '@components/EmptyRow'
import Flex from '@components/Flex'
import Spinner from '@components/Spinner'
import Table from '@components/Table'
import Td from '@components/Td'
import Tr from '@components/Tr'
import { leadApi } from '@services/LeadsApi'
import { openErrorModal } from '@store/ducks/modal'
import { useExportLeads } from '@store/hooks/useExportLeads'
import Formatters from '@utils/helpers/Formatters'
import { useQuery } from '@utils/hooks'

const styles = {
	searchContainer: 'gap-[8px] mb-[32px] mt-[32px] items-end flex-wrap',
	tag: 'font-medium text-blue-600 underline underline-offset-2',
	divider: 'mt-[32px] mb-[12px]',
	avatar: 'rounded-full max-w-[40px] max-h-[40px] min-w-[40px] min-h-[40px]',
}

function ListLeads() {
	const pages = [{ name: 'Leads', href: '#', current: true }]

	const query = useQuery()
	const navigate = useNavigate()

	const dispatch = useDispatch()

	const today = new Date()
	const yesterday = new Date(
		today.getFullYear(),
		today.getMonth(),
		today.getDate() - 1
	)

	const isDateValid = ({
		startDate,
		endDate,
	}: {
		startDate: string
		endDate: string
	}) => {
		if (startDate > endDate) {
			dispatch(
				openErrorModal({
					title: 'Erro',
					message: 'A data inicial não pode ser maior que a data final.',
				})
			)

			return false
		}

		return true
	}

	const formatDate = (date: Date) => {
		const year = date.getFullYear()
		const month = String(date.getMonth() + 1).padStart(2, '0')
		const day = String(date.getDate()).padStart(2, '0')

		return `${year}-${month}-${day}`
	}

	const pageQuery = Number(query.get('page')) || 1

	const { exportLeads } = useExportLeads()

	const startDateRef = useRef(query.get('start_date') || formatDate(yesterday))
	const [startDate, setStartDate] = useState(startDateRef.current)
	const endDateRef = useRef(query.get('end_date') || formatDate(today))
	const [endDate, setEndDate] = useState(endDateRef.current)
	const [page, setPage] = useState(pageQuery)

	const { data, isLoading } = leadApi.useListQuery({
		page,
		startDate,
		endDate,
	})

	const searchCallback = useCallback(() => {
		navigate('/leads?page=1')
	}, [navigate])

	useEffect(() => {
		searchCallback()
	}, [startDate, searchCallback])

	useEffect(() => {
		setPage(pageQuery)
	}, [pageQuery])

	return (
		<>
			<Template>
				<Breadcrumb pages={pages} />
				<PageTitle title='Usuários' />
				<Flex className={styles.searchContainer}>
					<InputGroup label='Data inicial' id='startDate'>
						<Input
							type='date'
							defaultValue={startDateRef.current}
							maxLength={4}
							onChange={(e) => {
								startDateRef.current = e.currentTarget.value
							}}
						/>
					</InputGroup>
					<InputGroup label='Data final' id='endDate'>
						<Input
							type='date'
							defaultValue={endDateRef.current}
							maxLength={4}
							onChange={(e) => {
								endDateRef.current = e.currentTarget.value
							}}
						/>
					</InputGroup>

					<Button
						onClick={() => {
							if (
								!isDateValid({
									startDate: startDateRef.current,
									endDate: endDateRef.current,
								})
							) {
								return
							}

							setStartDate(startDateRef.current)
							setEndDate(endDateRef.current)
						}}
					>
						Filtrar
					</Button>
					<Button
						onClick={() => {
							if (
								!isDateValid({
									startDate: startDateRef.current,
									endDate: endDateRef.current,
								})
							) {
								return
							}

							exportLeads({
								startDate,
								endDate,
							})
						}}
					>
						Exportar leads
					</Button>
				</Flex>

				{isLoading ? (
					<div className='flex justify-center'>
						<Spinner size='md' />
					</div>
				) : (
					<Table
						th={[
							{
								label: 'nome',
							},
							{ label: 'e-mail' },
							{ label: 'telefone' },
							{ label: 'CTA' },
							{ label: 'Clicou em' },
							{ label: 'Plano' },
						]}
					>
						{data?.data && data?.data?.length > 0 ? (
							data?.data.map(
								({
									id: userId,
									name,
									email,
									phone,
									action,
									clickDate,
									latestClassName,
								}) => (
									<Tr key={userId}>
										<Td className='w-[40%] px-6'>
											<Link to={`/usuarios/${userId}`}>
												<Flex className='items-center gap-4'>{name}</Flex>
											</Link>
										</Td>
										<Td>
											<Link to={`/usuarios/${userId}`}>
												<Flex className='items-center gap-4'>{email}</Flex>
											</Link>
										</Td>
										<Td>
											<Link to={`/usuarios/${userId}`}>
												<Flex className='items-center gap-4'>{phone}</Flex>
											</Link>
										</Td>
										<Td>
											<Link to={`/usuarios/${userId}`}>
												<Flex className='items-center gap-4'>{action}</Flex>
											</Link>
										</Td>
										<Td>
											<Link to={`/usuarios/${userId}`}>
												<Flex className='items-center gap-4'>
													{Formatters.createdAt(clickDate)}
												</Flex>
											</Link>
										</Td>
										<Td>
											<Link to={`/usuarios/${userId}`}>
												<Flex className='items-center gap-4'>
													{latestClassName}
												</Flex>
											</Link>
										</Td>
									</Tr>
								)
							)
						) : (
							<EmptyRow tdAmount={3} message='Nenhum usuário encontrado' />
						)}
					</Table>
				)}

				<Divider className={styles.divider} />
				{data?.data && data?.data?.length > 0 && (
					<Pagination
						pageCount={data?.meta.lastPage || 0}
						maxAmount={data?.meta.total || 0}
						perPage={data?.meta.perPage || 0}
					/>
				)}
			</Template>
		</>
	)
}

export default ListLeads
